import ClearIcon from '@mui/icons-material/Clear';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { useListContext } from 'react-admin';

/**
 * What the list is currently filtered by, and a way out of it.
 *
 * react-admin puts active filters in the toolbar as ordinary inputs, which
 * makes a filtered list look much like an unfiltered one - the difference
 * between "this product does not exist" and "this product is not in the tab
 * you are looking at" is a value in a box you have to notice. Naming each
 * condition and colouring it makes that difference visible, and each chip
 * clears just its own condition.
 */

export interface FilterLabels {
  /** Field name to a human label, and optionally a value formatter. */
  readonly [field: string]: { label: string; format?: (value: unknown) => string } | undefined;
}

export function ActiveFilters({
  labels = {},
  /** Filters the screen sets itself, which are not the user narrowing anything. */
  ignore = []
}: {
  labels?: FilterLabels;
  ignore?: readonly string[];
}) {
  const { filterValues, setFilters, displayedFilters } = useListContext();

  const active = Object.entries(filterValues ?? {}).filter(
    ([field, value]) =>
      !ignore.includes(field) && value != null && value !== '' && value !== false
  );

  if (active.length === 0) return null;

  const clear = (field: string) => {
    const next = { ...filterValues };
    delete next[field];
    setFilters(next, displayedFilters);
  };

  const clearAll = () => {
    const next = Object.fromEntries(
      Object.entries(filterValues ?? {}).filter(([field]) => ignore.includes(field))
    );
    setFilters(next, displayedFilters);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{
        my: 1,
        p: 1,
        borderRadius: 1,
        bgcolor: 'primary.50',
        border: '1px solid',
        borderColor: 'primary.light'
      }}
    >
      <FilterAltIcon fontSize="small" color="primary" />
      <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
        Zobrazuje sa filtrovaný zoznam
      </Typography>

      {active.map(([field, value]) => {
        const config = labels[field];
        const shown = config?.format?.(value) ?? String(value);

        return (
          <Chip
            key={field}
            size="small"
            color="primary"
            variant="outlined"
            label={`${config?.label ?? field}: ${shown}`}
            onDelete={() => clear(field)}
            deleteIcon={<ClearIcon />}
          />
        );
      })}

      <Button size="small" onClick={clearAll} startIcon={<ClearIcon />}>
        Zrušiť filtre
      </Button>
    </Stack>
  );
}
